import type { Rep } from './social.js';
// Parties and alliances, FFXI style: up to 6 in a party, up to 3 parties in an alliance (18 people).
// Groups live in the server's memory, like FFXI: they last while you're online (and through a short
// reconnect), and you leave your party when you log out.
//
// Finding people:
//   - Seeking: a solo player raises the "looking for a party" flag with a short note. Anyone can see
//     seekers in the Party Finder and send them an invite.
//   - Recruiting: a party leader lists the party with a note. Anyone solo can ask to join; the leader
//     accepts or declines.

export const PARTY_MAX = 6;
export const ALLIANCE_MAX = 3;
/** Invites and join requests expire after this long. */
export const OFFER_SECS = 60;
export const NOTE_MAX = 60;
/** Only people of your own faction can group with you (factions are your army). Turn off for cross-faction groups. */
export const PARTY_SAME_FACTION = true;

export interface MemberView { char: number; name: string; faction: number; zone: string; online: boolean; offlineMins?: number; rep?: Rep; job?: string; lv?: number;
  /** Their party settings, shown to everyone: drink a potion below this much health (%, 0 off), accept a Raise without asking; and whether they've fallen. */
  autoPotion?: number; autoRaise?: boolean; fallen?: boolean }
export interface PartyView { id: number; leader: number; recruit: string | null; members: MemberView[]; loot?: LootRules }
/** How a party shares what creatures drop (the leader sets it). Common things (materials, potions): everyone finds
 *  their own, or they go round in turn. Better things (uncommon gear and up): everyone rolls (Need, Greed or Pass),
 *  they go round in turn, or the leader hands them out. Gil is always shared evenly. */
export type LootCommon = 'own' | 'round';
export type LootRare = 'roll' | 'round' | 'leader';
export interface LootRules { common: LootCommon; rare: LootRare }
export const DEFAULT_LOOT: LootRules = { common: 'round', rare: 'roll' };
/** How long a roll stays open (seconds). */
export const ROLL_SECS = 30;

/** One row of the Party Finder. */
export interface SeekerRow { char: number; name: string; faction: number; zone: string; note: string; mins: number; rep?: Rep; job?: string; lv?: number }
export interface RecruitRow { party: number; leader: number; name: string; faction: number; zone: string; note: string; size: number; rep?: Rep }

export type OfferKind = 'party' | 'alliance' | 'request';

export type PartyOp =
  | { op: 'invite'; name?: string; char?: number }   // invite someone to your party (makes one if you're solo)
  | { op: 'request'; char: number }                  // ask a recruiting party's leader to let you in
  | { op: 'answer'; offer: number; yes: boolean }    // accept or decline an invite or a request
  | { op: 'leave' }
  | { op: 'kick'; char: number }
  | { op: 'promote'; char: number }
  | { op: 'seek'; on: boolean; note?: string }        // the "looking for a party" flag
  | { op: 'recruit'; on: boolean; note?: string }     // list your party in the finder
  | { op: 'ainvite'; name?: string; char?: number }  // party leader: invite another party (by its leader) to your alliance
  | { op: 'aleave' }                                 // party leader: take your party out of the alliance
  | { op: 'akick'; party: number }                   // alliance leader: remove a party
  | { op: 'finder' }
  | { op: 'loot'; common?: LootCommon; rare?: LootRare };   // party leader: how drops are shared                                // fetch the Party Finder lists

/**
 * Chat commands for groups, FFXI and WoW spellings:
 *   /invite name · /pcmd add name · /pcmd kick name · /pcmd leader name · /pcmd leave · /leave
 *   /acmd add name · /acmd leave · /seek [note] · /seek off · /follow [name] · /follow off
 * Names are resolved on the client against the party list (kick, leader) or sent as typed (add).
 */
export type GroupCommand =
  | { cmd: 'invite' | 'ainvite' | 'kick' | 'promote'; name: string }
  | { cmd: 'leave' | 'aleave' | 'finder' }
  | { cmd: 'friend'; name: string }
  | { cmd: 'seek'; on: boolean; note: string }
  | { cmd: 'follow'; name: string }                  // '' = the party leader
  | { cmd: 'unfollow' }
  | { cmd: 'find'; name: string }                    // '' = stop looking
  | { cmd: 'block' | 'unblock' | 'mute' | 'unmute' | 'report' | 'check'; name: string };

export function parseGroupCommand(line: string): GroupCommand | null | 'usage' {
  const t = line.trim(); if (!t.startsWith('/')) return null;
  const [c0, a = '', ...rest] = t.slice(1).split(/\s+/);
  const c = c0.toLowerCase(), arg = a.toLowerCase();
  if (c === 'invite' || c === 'inv' || c === 'pi') return a ? { cmd: 'invite', name: a } : 'usage';
  if (c === 'leave' || c === 'pleave') return { cmd: 'leave' };
  if (c === 'lfp' || c === 'seek') return arg === 'off' ? { cmd: 'seek', on: false, note: '' } : { cmd: 'seek', on: true, note: [a, ...rest].join(' ') };
  if (c === 'finder' || c === 'pf') return { cmd: 'finder' };
  if (c === 'follow' || c === 'f') return arg === 'off' ? { cmd: 'unfollow' } : { cmd: 'follow', name: a };
  if (c === 'unfollow' || c === 'stopfollow') return { cmd: 'unfollow' };
  if (c === 'find' || c === 'search' || c === 'where' || c === 'sea') return { cmd: 'find', name: arg === 'off' ? '' : a };
  if (c === 'block' || c === 'unblock' || c === 'mute' || c === 'unmute' || c === 'report') return a ? { cmd: c, name: a } : 'usage';
  if (c === 'check' || c === 'inspect' || c === 'card') return a ? { cmd: 'check', name: a } : 'usage';
  if (c === 'friend' || c === 'addfriend') return a ? { cmd: 'friend', name: a } : 'usage';
  if (c === 'pcmd') {
    const name = rest[0] ?? '';
    if (arg === 'add') return name ? { cmd: 'invite', name } : 'usage';
    if (arg === 'kick' || arg === 'remove') return name ? { cmd: 'kick', name } : 'usage';
    if (arg === 'leader') return name ? { cmd: 'promote', name } : 'usage';
    if (arg === 'leave' || arg === 'breakup') return { cmd: 'leave' };
    return 'usage';
  }
  if (c === 'acmd') {
    const name = rest[0] ?? '';
    if (arg === 'add') return name ? { cmd: 'ainvite', name } : 'usage';
    if (arg === 'leave' || arg === 'breakup') return { cmd: 'aleave' };
    return 'usage';
  }
  return null;
}
